import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, HelpCircle, Check, BarChart3, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/auth";
import { logError } from "@/lib/log";

const emptyForm = {
  text: "",
  options: ["", "", "", ""],
  correct_index: 0,
};

function QuestionStats({ q }) {
  const total = q.times_answered || 0;
  const correct = q.times_correct || 0;
  const incorrect = q.times_incorrect || 0;
  const pct = total > 0 ? Math.round((correct / total) * 100) : null;
  if (total === 0) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[11px] text-zinc-400 font-medium"
        data-testid={`question-stats-${q.id}`}
        title="Aún no contestada"
      >
        <BarChart3 className="h-3 w-3" />
        Sin contestar
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center text-[11px] font-medium"
      data-testid={`question-stats-${q.id}`}
      title={`Contestada ${total} veces`}
    >
      <span className="inline-flex items-center gap-1 text-zinc-600">
        <BarChart3 className="h-3 w-3" />
        {total}×
      </span>
      <span className="px-1.5 text-zinc-300">·</span>
      <span className="inline-flex items-center gap-1 text-emerald-700">
        <Check className="h-3 w-3" />
        {correct}
      </span>
      <span className="px-1.5 text-zinc-300">·</span>
      <span className="inline-flex items-center gap-1 text-red-700">
        <X className="h-3 w-3" />
        {incorrect}
      </span>
      {pct !== null && (
        <span
          className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold ${
            pct >= 70
              ? "bg-emerald-100 text-emerald-700"
              : pct >= 40
              ? "bg-amber-100 text-amber-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {pct}% aciertos
        </span>
      )}
    </span>
  );
}

export default function QuestionsManager() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get(`/questions`);
      setQuestions(res.data || []);
    } catch (e) {
      logError("No se pudieron cargar las preguntas", e);
      toast.error("No se pudieron cargar las preguntas.");
    } finally {
      setLoading(false);
    }
    // adminApi/logError/toast son imports estables; setters de useState
    // tambien son estables. Lint silenciado intencionalmente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (q) => {
    setEditingId(q.id);
    setForm({
      text: q.text,
      options: [...q.options],
      correct_index: q.correct_index,
    });
    setDialogOpen(true);
  };

  const updateOption = (idx, value) => {
    const next = [...form.options];
    next[idx] = value;
    setForm({ ...form, options: next });
  };

  const handleSave = async () => {
    if (!form.text.trim()) {
      toast.error("La pregunta no puede estar vacía.");
      return;
    }
    if (form.options.some((o) => !o.trim())) {
      toast.error("Todas las alternativas deben tener texto.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        text: form.text.trim(),
        options: form.options.map((o) => o.trim()),
        correct_index: form.correct_index,
      };
      if (editingId) {
        await adminApi.put(`/questions/${editingId}`, payload);
        toast.success("Pregunta actualizada.");
      } else {
        await adminApi.post(`/questions`, payload);
        toast.success("Pregunta creada.");
      }
      setDialogOpen(false);
      await fetchQuestions();
    } catch (e) {
      toast.error("Error al guardar la pregunta.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminApi.delete(`/questions/${deleteTarget.id}`);
      toast.success("Pregunta eliminada.");
      setDeleteTarget(null);
      await fetchQuestions();
    } catch (e) {
      toast.error("No se pudo eliminar.");
    }
  };

  const letters = ["A", "B", "C", "D"];

  return (
    <section data-testid="questions-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="heading-font text-2xl font-bold text-zinc-950">
            Banco de preguntas
          </h2>
          <p className="text-sm text-zinc-600 mt-1">
            {questions.length}{" "}
            {questions.length === 1 ? "pregunta" : "preguntas"} en el banco
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="h-11 px-5 bg-zinc-950 hover:bg-zinc-800 text-white rounded-md"
          data-testid="new-question-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nueva pregunta
        </Button>
      </div>

      {loading ? (
        <div className="bg-white border border-zinc-200 rounded-lg p-10 text-center text-zinc-500">
          Cargando…
        </div>
      ) : questions.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center bg-zinc-100 rounded-md mb-4">
            <HelpCircle className="h-5 w-5 text-zinc-500" />
          </div>
          <h3 className="heading-font text-xl font-bold text-zinc-950">
            Aún no hay preguntas
          </h3>
          <p className="text-sm text-zinc-600 mt-2">
            Crea tu primera pregunta tipo test (4 alternativas) para el banco
            de exámenes.
          </p>
          <Button
            onClick={openCreate}
            className="mt-6 h-11 px-5 bg-zinc-950 hover:bg-zinc-800 text-white rounded-md"
            data-testid="empty-create-question-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Crear primera pregunta
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {questions.map((q, idx) => (
            <div
              key={q.id}
              className="bg-white border border-zinc-200 rounded-lg p-5 md:p-6"
              data-testid={`question-card-${q.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1 flex-wrap">
                    <div className="text-xs uppercase tracking-[0.14em] text-zinc-500 font-semibold">
                      Pregunta #{idx + 1}
                    </div>
                    <QuestionStats q={q} />
                  </div>
                  <p className="text-zinc-950 font-semibold leading-snug">
                    {q.text}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEdit(q)}
                    className="h-9 border border-zinc-300 hover:bg-zinc-50"
                    data-testid={`edit-question-${q.id}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setDeleteTarget(q)}
                    className="h-9 border border-zinc-300 hover:bg-red-50 hover:border-red-300 hover:text-red-700"
                    data-testid={`delete-question-${q.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
                {q.options.map((opt, oi) => {
                  const correct = oi === q.correct_index;
                  return (
                    <li
                      key={`${q.id}-opt-${oi}`}
                      className={`flex items-start gap-2 rounded-md border p-3 text-sm ${
                        correct
                          ? "border-zinc-950 bg-zinc-50"
                          : "border-zinc-200 bg-white"
                      }`}
                    >
                      <span
                        className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-[11px] font-bold ${
                          correct
                            ? "bg-zinc-950 text-white"
                            : "bg-zinc-100 text-zinc-700"
                        }`}
                      >
                        {correct ? <Check className="h-3 w-3" /> : letters[oi]}
                      </span>
                      <span className="text-zinc-800 leading-snug">{opt}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="question-dialog"
        >
          <DialogHeader>
            <DialogTitle className="heading-font text-2xl">
              {editingId ? "Editar pregunta" : "Nueva pregunta"}
            </DialogTitle>
            <DialogDescription>
              Introduce el enunciado, las 4 alternativas y marca cuál es la
              correcta.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Enunciado
              </label>
              <Textarea
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                placeholder="Escribe la pregunta…"
                rows={3}
                className="border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                data-testid="question-text-input"
                autoFocus
              />
            </div>

            <div>
              <div className="block text-sm font-medium text-zinc-700 mb-3">
                Alternativas{" "}
                <span className="text-zinc-500 font-normal">
                  (marca la correcta a la izquierda)
                </span>
              </div>
              <RadioGroup
                value={String(form.correct_index)}
                onValueChange={(v) =>
                  setForm({ ...form, correct_index: parseInt(v, 10) })
                }
              >
                <div className="space-y-3">
                  {form.options.map((opt, idx) => (
                    <div
                      key={`form-opt-${idx}`}
                      className="flex items-center gap-3"
                      data-testid={`question-option-row-${idx}`}
                    >
                      <RadioGroupItem
                        value={String(idx)}
                        id={`opt-${idx}`}
                        className="h-5 w-5 border-2 border-zinc-300 text-zinc-950"
                        data-testid={`question-correct-radio-${idx}`}
                      />
                      <span className="w-6 text-sm font-bold text-zinc-700">
                        {letters[idx]}
                      </span>
                      <Input
                        value={opt}
                        onChange={(e) => updateOption(idx, e.target.value)}
                        placeholder={`Alternativa ${letters[idx]}`}
                        className="h-11 flex-1 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                        data-testid={`question-option-input-${idx}`}
                      />
                    </div>
                  ))}
                </div>
              </RadioGroup>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={saving}
              className="border border-zinc-300"
              data-testid="question-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="question-save-button"
            >
              {saving
                ? "Guardando…"
                : editingId
                ? "Guardar cambios"
                : "Crear pregunta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent data-testid="delete-question-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar pregunta?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-question-cancel">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-question-confirm"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
